import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { PlusIcon } from "@heroicons/react/24/outline";
import fileFormatService from "../../services/fileFormatService";
import FileFormatCard, { FORMAT_META, ToggleSwitch, apiErrorMessage } from "./FileFormatCard";

const FORMAT_ORDER = ["docx", "pdf", "xlsx", "pptx", "csv", "md", "txt"];

const SwitchCard = ({ title, subtitle, isActive, disabled, onToggle }) => (
  <div className="flex items-center gap-3 rounded-xl border border-gray-100 px-4 py-3">
    <div className="text-right">
      <p className="text-sm font-semibold text-gray-900">{title}</p>
      <p className="text-xs text-gray-500">{subtitle}</p>
    </div>
    <ToggleSwitch label={title} isActive={isActive} disabled={disabled} onToggle={onToggle} />
  </div>
);

// Inline "Add template" form for one format: name, description and the
// template file (required for every format that has one; PDF is style-only).
const AddTemplateForm = ({ facilityNumber, fileFormat, onCreated, onCancel }) => {
  const meta = FORMAT_META[fileFormat];
  const fileRef = useRef(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState(null);
  // Never pre-ticked: making a template the default is always a deliberate choice.
  const [makeDefault, setMakeDefault] = useState(false);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!name.trim()) {
      toast.error("Template name is required.");
      return;
    }
    if (meta.accept && !file) {
      toast.error(`Choose the ${meta.ext} template file to upload.`);
      return;
    }
    if (file && (fileFormat === "md" || fileFormat === "txt")) {
      const text = await file.text();
      if (!text.includes("{{CONTENT}}")) {
        toast.error("Text templates must contain the {{CONTENT}} placeholder where generated content goes.");
        return;
      }
    }
    setSaving(true);
    try {
      const created = await fileFormatService.createTemplate(facilityNumber, {
        fileFormat,
        name: name.trim(),
        description: description.trim(),
        isDefault: makeDefault,
      });
      if (file) {
        try {
          await fileFormatService.uploadTemplateFile(facilityNumber, created.id, file);
        } catch (error) {
          // Don't leave a template without its file behind: roll it back.
          const message = await apiErrorMessage(error);
          await fileFormatService.deleteTemplate(facilityNumber, created.id).catch(() => {});
          toast.error(`Template not added — the file was rejected: ${message}`);
          return;
        }
      }
      toast.success(`"${created.name}" added. Enable it when it's ready.`);
      onCreated();
    } catch (error) {
      toast.error(await apiErrorMessage(error, "Failed to add template"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3 rounded-xl border border-dashed border-[#2B3674]/30 bg-[#2B3674]/[0.02] p-4">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <input
          type="text"
          maxLength={120}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={`Name, e.g. ${fileFormat === "pptx" ? "Product demo" : "Sales proposal"}`}
          className="rounded-lg border border-gray-200 px-3.5 py-2.5 text-sm text-gray-800 focus:border-[#2B3674] focus:outline-none"
        />
        <input
          type="text"
          maxLength={1000}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Description shown to users (optional)"
          className="rounded-lg border border-gray-200 px-3.5 py-2.5 text-sm text-gray-800 focus:border-[#2B3674] focus:outline-none"
        />
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-4">
          {meta.accept && (
            <>
              <input
                ref={fileRef}
                type="file"
                accept={meta.accept}
                className="hidden"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
              />
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                className="rounded-lg border border-gray-200 px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50"
              >
                {file ? `File: ${file.name}` : `Choose ${meta.ext} file (required)`}
              </button>
            </>
          )}
          <label className="flex items-center gap-2 text-xs text-gray-700">
            <input type="checkbox" checked={makeDefault} onChange={(e) => setMakeDefault(e.target.checked)} />
            Make this the default {meta.ext} template
          </label>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-xl border border-gray-200 px-4 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={saving || !name.trim() || (meta.accept && !file)}
            onClick={submit}
            className="rounded-xl bg-[#2B3674] px-4 py-2 text-xs font-semibold text-white hover:bg-blue-800 disabled:opacity-50"
          >
            {saving ? "Adding..." : "Add template"}
          </button>
        </div>
      </div>
    </div>
  );
};

// Superadmin-only: per-facility named templates for AI-generated files.
// A template applies only when "Apply facility formatting" and the template's
// own switch are on: the user's pick (if selection is enabled), else the
// format's default, else none.
const FileFormatsTab = ({ facility }) => {
  const facilityNumber = facility?.facility_number;
  const [overview, setOverview] = useState(null);
  const [schema, setSchema] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [toggling, setToggling] = useState(null);
  const [addingFor, setAddingFor] = useState(null);
  const [activeFormat, setActiveFormat] = useState(null);

  const loadOverview = useCallback(async () => {
    if (!facilityNumber) return;
    try {
      const data = await fileFormatService.getFacilityTemplates(facilityNumber);
      setOverview(data);
      setLoadError(null);
    } catch (error) {
      setLoadError(await apiErrorMessage(error, "Failed to load file templates"));
    }
  }, [facilityNumber]);

  useEffect(() => {
    setIsLoading(true);
    Promise.all([
      loadOverview(),
      fileFormatService
        .getSchema()
        .then(setSchema)
        .catch((error) => console.error("Error fetching file format schema:", error)),
    ]).finally(() => setIsLoading(false));
  }, [loadOverview]);

  const SWITCHES = {
    enabled: {
      field: "file_formatting_enabled",
      save: fileFormatService.setFormattingEnabled,
      message: (next) => `File formatting ${next ? "enabled" : "disabled"} for ${facility?.name || "this facility"}.`,
    },
    selection: {
      field: "file_template_selection_enabled",
      save: fileFormatService.setSelectionEnabled,
      message: (next) => `Users ${next ? "can now" : "can no longer"} choose a template in chat.`,
    },
    skills: {
      field: "file_template_skills_enabled",
      save: fileFormatService.setSkillsEnabled,
      message: (next) =>
        next
          ? "Enhanced design on: templated files are designed by skills."
          : "Enhanced design off: templated files render instantly from the template.",
    },
  };

  const toggle = async (which) => {
    const sw = SWITCHES[which];
    const next = !overview?.[sw.field];
    setToggling(which);
    try {
      const data = await sw.save.call(fileFormatService, facilityNumber, next);
      setOverview(data);
      toast.success(sw.message(next));
    } catch (error) {
      toast.error(await apiErrorMessage(error, "Failed to update setting"));
    } finally {
      setToggling(null);
    }
  };

  if (isLoading) {
    return <div className="py-16 text-center text-gray-500">Loading file templates...</div>;
  }

  if (loadError) {
    return (
      <div className="py-16 text-center">
        <p className="text-sm text-red-600">{loadError}</p>
        <button
          type="button"
          onClick={() => {
            setIsLoading(true);
            loadOverview().finally(() => setIsLoading(false));
          }}
          className="mt-3 rounded-xl border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
        >
          Retry
        </button>
      </div>
    );
  }

  const templates = overview?.templates || [];
  const masterOn = Boolean(overview?.file_formatting_enabled);
  const selectionOn = Boolean(overview?.file_template_selection_enabled);
  const skillsOn = overview?.file_template_skills_enabled !== false;
  const enabledCount = templates.filter((t) => t.is_enabled).length;
  const formats = (schema?.formats || FORMAT_ORDER).filter((fmt) => FORMAT_META[fmt]);
  // First tab: the chosen one, else the first type that has templates, else the first type.
  const currentFormat =
    (activeFormat && formats.includes(activeFormat) && activeFormat) ||
    formats.find((fmt) => templates.some((t) => t.file_format === fmt)) ||
    formats[0];

  return (
    <div>
      <div className="mb-5 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h3 className="text-base font-bold text-gray-900">File templates</h3>
          <p className="text-sm text-gray-500">
            Named templates applied when the AI generates files for this facility
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <SwitchCard
            title="Apply facility formatting"
            subtitle={masterOn ? `${enabledCount} template${enabledCount === 1 ? "" : "s"} enabled` : "Off — default formatting is used"}
            isActive={masterOn}
            disabled={toggling !== null}
            onToggle={() => toggle("enabled")}
          />
          <SwitchCard
            title="Let users choose a template in chat"
            subtitle={selectionOn ? "Template picker shown in the chat" : "Only default templates apply"}
            isActive={selectionOn}
            disabled={toggling !== null}
            onToggle={() => toggle("selection")}
          />
          <SwitchCard
            title="Enhanced design (skills)"
            subtitle={skillsOn ? "Skills design, template branding · slower" : "Instant render from the template"}
            isActive={skillsOn}
            disabled={toggling !== null}
            onToggle={() => toggle("skills")}
          />
        </div>
      </div>

      {!masterOn && (
        <div className="mb-4 rounded-xl border border-amber-100 bg-amber-50 px-4 py-3 text-xs text-amber-800">
          Facility formatting is off. You can still add, configure and preview templates — they apply once this switch
          and the template's own switch are both on.
        </div>
      )}

      {/* One tab per file type; the active type's templates live in one box. */}
      <div role="tablist" aria-label="File types" className="mb-3 flex flex-wrap gap-2 border-b border-gray-100 pb-3">
        {formats.map((fmt) => {
          const group = templates.filter((t) => t.file_format === fmt);
          const active = fmt === currentFormat;
          return (
            <button
              key={fmt}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => {
                setActiveFormat(fmt);
                setAddingFor(null);
              }}
              className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-semibold transition-colors ${
                active ? "bg-[#2B3674] text-white" : "border border-gray-200 text-gray-700 hover:bg-gray-50"
              }`}
            >
              {FORMAT_META[fmt].label}
              <span
                className={`rounded-full px-1.5 text-[10px] font-bold ${
                  active ? "bg-white/20 text-white" : "bg-gray-100 text-gray-600"
                }`}
              >
                {group.length}
              </span>
              {group.some((t) => t.is_default) && (
                <span
                  title="Default set"
                  className={`h-1.5 w-1.5 rounded-full ${active ? "bg-emerald-300" : "bg-emerald-500"}`}
                />
              )}
            </button>
          );
        })}
      </div>

      {(() => {
        const fmt = currentFormat;
        const meta = FORMAT_META[fmt];
        const group = templates.filter((t) => t.file_format === fmt);
        const hasDefault = group.some((t) => t.is_default);
        return (
          <section role="tabpanel" className="rounded-2xl border border-gray-100 p-4">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <span className="flex h-8 w-12 items-center justify-center rounded-lg bg-gray-100 text-xs font-bold text-gray-600">
                  {meta.ext}
                </span>
                <div>
                  <p className="text-sm font-semibold text-gray-900">{meta.label} templates</p>
                  <p className="text-xs text-gray-500">
                    {group.length
                      ? `${group.length} template${group.length === 1 ? "" : "s"} · ${hasDefault ? "default set" : "no default — nothing applies unless a user picks one"}`
                      : "No templates yet"}
                  </p>
                </div>
              </div>
              {addingFor !== fmt && (
                <button
                  type="button"
                  onClick={() => setAddingFor(fmt)}
                  className="inline-flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50"
                >
                  <PlusIcon className="h-4 w-4" /> Add template
                </button>
              )}
            </div>
            <p className="mb-3 text-xs text-gray-500">{meta.hint}</p>
            <div className="space-y-3">
              {addingFor === fmt && (
                <AddTemplateForm
                  key={fmt}
                  facilityNumber={facilityNumber}
                  fileFormat={fmt}
                  onCancel={() => setAddingFor(null)}
                  onCreated={() => {
                    setAddingFor(null);
                    loadOverview();
                  }}
                />
              )}
              {group.map((row) => (
                <FileFormatCard
                  key={row.id}
                  facilityNumber={facilityNumber}
                  row={row}
                  schema={schema}
                  onChanged={loadOverview}
                />
              ))}
              {!group.length && addingFor !== fmt && (
                <p className="rounded-xl border border-dashed border-gray-200 px-4 py-6 text-center text-xs text-gray-500">
                  No {meta.label} templates. Use “Add template” to upload one.
                </p>
              )}
            </div>
          </section>
        );
      })()}
    </div>
  );
};

export default FileFormatsTab;
